import cx from "classnames";
/* eslint-disable max-lines */
import React from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { Column, format, Row, useCurrentViewPort, ViewPort } from "@gamedev-web/gamedev-sdk-core";
import { BasicButton, ButtonType, Icon, IconSize } from "@gaming/xds-react-js";
import { ApplicationLaunch } from "@gaming/xds-react-js-icons";

import { PageType, partnerCenterUrl } from "../../constants";
import { getImage, ImageType } from "../../helpers/imageHelper";
import { useIsIdAzureWelcomePageEnabled } from "../../helpers/navigationHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { OnboardingRootState } from "../../redux/reducers/rootReducer";
import { getIsInitialCrmFetchDoneByProgram } from "../../redux/selectors";
import { getIsPitchEnabled } from "../../redux/selectors/capabilitiesSelectors";
import { OnboardingPageAction } from "../../telemetry/models";
import { logPageAction } from "../../telemetry/pageAction";
import * as colors from "../commonStyles/colors.module.css";
import { ContentBox, ContentBoxIcon } from "../ContentBox";
import { AttributesTable } from "./AttributesTable";
import * as styles from "./ProgramSelector.module.css";
import { idAzureName, idXboxName, ProgramColumnConfig } from "./programSelectorUtils";

const renderProgramSelectionSignedIn = (
  logoImage: string,
  logoImageAltText: string,
  selectProgramFormat: string,
  programName: string,
  programId: string,
  programDescription: string,
  cost: string,
  columnsConfig: ProgramColumnConfig[],
  learnMoreLabel: string,
  onClick: () => void,
  currentViewPort: ViewPort,
  buttonClass?: string,
  useLink?: boolean,
  customButtonName?: string,
  disabledButton?: boolean
) => {
  const programImage = <img className={styles.image} src={logoImage} alt={logoImageAltText} />;
  const titleAndButton = (
    <>
      <Column className={styles.largeColumn}>
        <h3 className={styles.programName}>{programName}</h3>
      </Column>
      <Column>
        {!useLink && (
          <BasicButton
            as={"button"}
            buttonType={disabledButton ? ButtonType.Secondary : ButtonType.Brand}
            onClick={onClick}
            className={cx(styles.button, buttonClass ?? "")}
            textDecorationUnderline={true}
            disabled={disabledButton}
          >
            {customButtonName ?? format(selectProgramFormat, programName)}
          </BasicButton>
        )}
        {useLink && (
          <BasicButton
            as={"button"}
            onClick={onClick}
            className={styles.rightSideLink}
            aria-label={`${learnMoreLabel} ${programName} ${cost}`}
          >
            {learnMoreLabel}
            <Icon icon={ApplicationLaunch} iconSize={IconSize.Custom} />
          </BasicButton>
        )}
      </Column>
    </>
  );
  const descriptionAndPrice = (
    <Column>
      <Row className={styles.rowProgramDescriptionPriceContainer}>
        <Column className={styles.largeColumn}>
          <p>{programDescription}</p>
        </Column>
        <Column>
          <p className={styles.cost}>{cost}</p>
        </Column>
      </Row>
    </Column>
  );
  return (
    <ContentBox className={styles.programBox}>
      {currentViewPort >= ViewPort.MD && (
        <Row>
          <Column className={styles.imageColumn}>{programImage}</Column>
          <Column className={styles.programInfoWrapper}>
            <Row>{titleAndButton}</Row>
            <Row>{descriptionAndPrice}</Row>
            <AttributesTable columnsConfig={columnsConfig} programId={programId} />
          </Column>
        </Row>
      )}
      {currentViewPort < ViewPort.MD && (
        <>
          <Row>{programImage}</Row>
          <Row>
            <Column className={styles.programInfoWrapper}>
              <Row>{titleAndButton}</Row>
              <Row>{descriptionAndPrice}</Row>
              <AttributesTable columnsConfig={columnsConfig} programId={programId} />
            </Column>
          </Row>
        </>
      )}
    </ContentBox>
  );
};

export const SignedInNoSelectedProgram = () => {
  const [
    chooseAProgram,
    otherOpportunities,
    selectProgramFormat,
    creatorsProgram,
    idXboxProgramDescription,
    idAzureProgramDescription,
    creatorsProgramDescription,
    idXboxCost,
    idAzureCost,
    creatorsProgramCost,
    directSupport,
    devKits,
    approvals,
    potentialEndpoints,
    idXboxDirectSupport,
    idXboxDevKits,
    idXboxApprovals,
    idXboxPotentialEndpoints,
    idAzureDirectSupport,
    freeServices,
    idAzureFreeServices,
    askTheExperts,
    idAzureAskTheExperts,
    idAzurePotentialEndpoints,
    creatorsProgramDirectSupport,
    creatorsProgramDevKits,
    creatorsProgramApprovals,
    creatorsProgramPotentialEndpoints,
    learnMore,
    pitchProgram,
    pitchProgramDescription,
    pitchProgramButtonSelectProgram,
    pitchProgramUnlockedButtonSelectProgram,
    idXboxProgramImageAltText,
    idAzureProgramImageAltText,
    pitchProgramImageAltText,
    creatorsProgramImageAltText,
  ] = useStrings(
    "chooseAProgram",
    "otherOpportunities",
    "selectProgramFormat",
    "creatorsProgram",
    "idXboxProgramDescription",
    "idAzureProgramDescription",
    "creatorsProgramDescription",
    "idXboxCost",
    "idAzureCost",
    "creatorsProgramCost",
    "directSupport",
    "devKits",
    "approvals",
    "potentialEndpoints",
    "idXboxDirectSupport",
    "idXboxDevKits",
    "idXboxApprovals",
    "idXboxPotentialEndpoints",
    "idAzureDirectSupport",
    "freeServices",
    "idAzureFreeServices",
    "askTheExperts",
    "idAzureAskTheExperts",
    "idAzurePotentialEndpoints",
    "creatorsProgramDirectSupport",
    "creatorsProgramDevKits",
    "creatorsProgramApprovals",
    "creatorsProgramPotentialEndpoints",
    "learnMore",
    "pitchProgram",
    "pitchProgramDescription",
    "pitchProgramButtonSelectProgram",
    "pitchProgramUnlockedButtonSelectProgram",
    "idXboxProgramImageAltText",
    "idAzureProgramImageAltText",
    "pitchProgramImageAltText",
    "creatorsProgramImageAltText"
  );
  const isPitchEnabled = useSelector(getIsPitchEnabled);
  const history = useHistory();

  const creatorsProgramOnClick = () => (window.location.href = partnerCenterUrl);
  const currentViewPort = useCurrentViewPort();

  // ID@Azure
  const isInitialIdAzureCrmFetchDone = useSelector((state: OnboardingRootState) =>
    getIsInitialCrmFetchDoneByProgram(state, PageType.IdAzureApplication)
  );
  const isIdAzureWelcomePageEnabled = useIsIdAzureWelcomePageEnabled();

  const idXboxOnClick = () => {
    logPageAction(OnboardingPageAction.SelectIdXboxButton);
    history.push(OnboardingUrls[RouteNames.IdXboxWelcome]);
  };
  const idAzureOnClick = () => {
    logPageAction(OnboardingPageAction.SelectIdAzureButton);
    history.push(
      OnboardingUrls[
        isInitialIdAzureCrmFetchDone && !isIdAzureWelcomePageEnabled
          ? RouteNames.IdAzureHome
          : RouteNames.IdAzureWelcome
      ]
    );
  };

  const pitchOnClick = () => {
    logPageAction(OnboardingPageAction.SelectPitchButton);
    history.push(OnboardingUrls[RouteNames.PitchWelcome]);
  };

  return (
    <>
      <ContentBox
        titleConfiguration={{
          title: chooseAProgram,
          icon: ContentBoxIcon.GreenCircle,
          separatorBar: true,
        }}
      >
        {renderProgramSelectionSignedIn(
          getImage("idxboxlogo", ImageType.Png),
          idXboxProgramImageAltText,
          selectProgramFormat,
          idXboxName,
          "idxbox",
          idXboxProgramDescription,
          idXboxCost,
          [
            {
              title: directSupport,
              content: idXboxDirectSupport,
            },
            {
              title: devKits,
              content: idXboxDevKits,
            },
            {
              title: approvals,
              content: idXboxApprovals,
              doubleSizedColumn: true,
            },
            {
              title: potentialEndpoints,
              content: idXboxPotentialEndpoints,
            },
          ],
          learnMore,
          idXboxOnClick,
          currentViewPort
        )}
        {renderProgramSelectionSignedIn(
          getImage("idazurelogo", ImageType.Png),
          idAzureProgramImageAltText,
          selectProgramFormat,
          idAzureName,
          "idazure",
          idAzureProgramDescription,
          idAzureCost,
          [
            {
              title: directSupport,
              content: idAzureDirectSupport,
            },
            {
              title: freeServices,
              content: idAzureFreeServices,
            },
            {
              title: askTheExperts,
              content: idAzureAskTheExperts,
            },
            {
              title: potentialEndpoints,
              content: idAzurePotentialEndpoints,
            },
          ],
          learnMore,
          idAzureOnClick,
          currentViewPort,
          cx(colors.azureBlueBackgroundColor, colors.azureBlueHoverBackgroundColor)
        )}
        {renderProgramSelectionSignedIn(
          getImage("creatorslogo", ImageType.Png),
          creatorsProgramImageAltText,
          selectProgramFormat,
          creatorsProgram,
          "creators",
          creatorsProgramDescription,
          creatorsProgramCost,
          [
            {
              title: directSupport,
              content: creatorsProgramDirectSupport,
            },
            {
              title: devKits,
              content: creatorsProgramDevKits,
            },
            {
              title: approvals,
              content: creatorsProgramApprovals,
              doubleSizedColumn: true,
            },
            {
              title: potentialEndpoints,
              content: creatorsProgramPotentialEndpoints,
            },
          ],
          learnMore,
          creatorsProgramOnClick,
          currentViewPort,
          undefined,
          true
        )}
      </ContentBox>
      <ContentBox
        titleConfiguration={{
          title: otherOpportunities,
          separatorBar: true,
        }}
      >
        {renderProgramSelectionSignedIn(
          getImage("pitchlogo", ImageType.Png),
          pitchProgramImageAltText,
          selectProgramFormat,
          pitchProgram,
          "pitch",
          pitchProgramDescription,
          "",
          [],
          learnMore,
          pitchOnClick,
          currentViewPort,
          undefined,
          undefined,
          isPitchEnabled
            ? pitchProgramUnlockedButtonSelectProgram
            : pitchProgramButtonSelectProgram,
          !isPitchEnabled
        )}
      </ContentBox>
    </>
  );
};
